<i18n
  src="@/locales/views/organization/partners/producers/show_producer.yml"
  lang="yaml"
></i18n>
<i18n src="@/locales/models/producer.yml" lang="yaml"></i18n>
<i18n src="@/locales/models/crop_field.yml" lang="yaml"></i18n>

<template>
  <go-back-link :text="$t('labels.go_back_link')" name="org_partners_producers_list_producers_path" :params="{
    organization_id: organization_id
  }" />

  <bread-crumbs :labels="[module_name, producer?.organization_producer?.producer_code]" />

  <alert-danger :errors_data="std_err" />

  <template v-if="load_general_data">
    <div class="mt-8">
      <div class="panel">
        <div class="panel-header">
          <h3 class="panel-header-title">
            {{ $t("labels.title") }}
          </h3>
        </div>

        <div class="panel-body">
          <div class="space-y-8 divide-y divide-slate-200">
            <div class="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
              <div class="sm:col-span-3">
                <dt class="label-data">
                  {{ $t("model.producer.organization_producer.producer_code") }}
                </dt>
                <dd class="content-data">
                  {{ producer.organization_producer.producer_code }}
                  <span class="space-icon" />
                  <yes-no-label :value="producer.organization_producer.active" yes="Activo" no="Inactivo" />
                  <span class="space-icon" />
                  <o-p-formal-label :value="producer.organization_producer.formalized" />
                </dd>
              </div>

              <div class="sm:col-span-3">
                <dt class="label-data">
                  {{ $t("model.producer.producer_code") }}
                </dt>
                <dd class="content-data">
                  {{ producer.producer_code }}
                </dd>
              </div>

              <div class="sm:col-span-3">
                <dt class="label-data">
                  {{ $t("model.producer.lastname") }}
                </dt>
                <dd class="content-data">
                  {{ producer.lastname }}
                </dd>
              </div>

              <div class="sm:col-span-3">
                <dt class="label-data">
                  {{ $t("model.producer.name") }}
                </dt>
                <dd class="content-data">
                  {{ producer.name }}
                </dd>
              </div>

              <div class="sm:col-span-3">
                <dt class="label-data">
                  {{ $t("model.producer.gender_id") }}
                </dt>
                <dd class="content-data">
                  {{ genderName(producer.gender_id) }}
                </dd>
              </div>

              <div class="sm:col-span-3">
                <dt class="label-data">
                  {{ $t("model.producer.birth_date") }}
                </dt>
                <dd class="content-data">
                  {{ dateFormatter(producer.birth_date) }}
                </dd>
              </div>

              <div class="sm:col-span-3">
                <dt class="label-data">
                  {{ $t("model.producer.producer_crops") }}
                </dt>
                <dd class="content-data">
                  {{ producer_crops }}
                </dd>
              </div>
              <div v-for="(attribute_producer, index) in attribute_producers" :key="index" class="sm:col-span-3">
                <dt class="label-data">
                  {{ attribute_producer.label }}
                </dt>
                <dd class="content-data">
                  <yes-no-label v-if="isBoolean(attribute_producer.datatype_id)" :value="Boolean(
                    Number(producerAttributeValue(attribute_producer.id)?.value)
                  )
                    ">
                  </yes-no-label>
                  <span v-if="isString(attribute_producer.datatype_id)">
                    {{ producerAttributeValue(attribute_producer.id)?.value }}
                  </span>
                  <span
                    v-if="isImage(attribute_producer.datatype_id)"
                    v-html="producerAttributeValueImage(attribute_producer)">
                  </span>
                </dd>
              </div>
            </div>
          </div>
        </div>
        <div class="panel-footer">
          <div class="panel-footer-actions">
            <destroy-button v-if="!proc_button_destroy" :disabled_button="false" @destroyEvent="destroyProducer()"
              :label="$t('labels.destroy_button_label')" :title="$t('labels.destroy_button_title')"
              :message="$t('labels.destroy_button_message')" :label_confirm="$t('labels.destroy_button_label_confirm')" />
            <destroy-button-processing v-else :label="$t('labels.destroy_button_label')" />
            <span class="space-actions"></span>
            <edit-button @click.prevent="goToEditProducer()" :label="$t('labels.edit_button')" />
          </div>
        </div>
      </div>
    </div>

    <div class="my-8">
      <div class="flex justify-between mt-8 mb-2">
        <h3 class="items-center">
          <div class="panel-header-title">{{ $t("labels.crop_fields") }}</div>
        </h3>

        <new-button @click.prevent="goToNewCropField" :label="$t('labels.new_crop_field_button')" />
      </div>

      <div class="mt-8 flex flex-col">
        <ag-grid-vue class="ag-theme-alpine" :columnDefs="columnDefs" :defaultColDef="defaultColDef"
          :rowData="crop_fields" domLayout="autoHeight">
        </ag-grid-vue>
      </div>
    </div>
  </template>

  <loading-message v-else />
</template>

<script>
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { AgGridVue } from "ag-grid-vue3";
import { baseDomain } from '@/components/api/v1/requester.js';
import Requester from "@/views/organization/requesters/partners/producers/manage_producer.js";
import RequesterManageCropField from "@/views/organization/requesters/partners/producers/manage_crop_field.js";
import GenderMixin from "@/components/views/mixins/organization/genders.js";
import DatatypeMixin from "@/components/views/mixins/organization/datatypes.js";
import TableActions from "@/components/views/organization/partners/crop_fields/TableActions.vue";
import FormerProducer from "@/components/views/organization/partners/crop_fields/FormerProducer.vue";
import OPFormalLabel from '@/components/views/organization/partners/producers/FormalLabel.vue'

export default {
  name: "OrgPartnerProducersShowProducer",
  components: { AgGridVue, OPFormalLabel },
  mixins: [GenderMixin, DatatypeMixin],
  data() {
    return {
      load_general_data: false,
      producer: {},
      attribute_producers: [],
      crop_fields: [],
      proc_button_destroy: false,
      defaultColDef: {
        sortable: true,
        filter: true,
        floatingFilter: true
      },
      columnDefs: [
        {
          headerName: this.$t(
            "model.crop_field.organization_crop_field.active"
          ),
          field: "organization_crop_field.active",
          width: 100,
          filter: true,
          cellRendererSelector: params => {
            return {
              component: {
                template: `<yes-no-label :value="${params.value}"></yes-no-label>`
              }
            };
          }
        },
        {
          headerName: this.$t(
            "model.crop_field.organization_crop_field.crop_field_code"
          ),
          field: "organization_crop_field.crop_field_code",
          filter: true
        },
        {
          headerName: this.$t("model.crop_field.name"),
          field: "name",
          filter: true
        },
        {
          headerName: this.$t("model.crop_field.hectares"),
          field: "hectares",
          filter: "agNumberColumnFilter",
          cellRenderer: params => {
            return this.numberWithPrecision(params.value);
          }
        },
        {
          headerName: this.$t("model.crop_field.square_meters"),
          field: "square_meters",
          filter: "agNumberColumnFilter",
          cellRenderer: params => {
            return this.numberWithPrecision(params.value);
          }
        },
        {
          headerName: this.$t("model.crop_field.location"),
          field: "organization_crop_field.crop_field_zone.name",
          filter: true
        },
        {
          headerName: this.$t("model.crop_field.crop_id"),
          field: "crop.name",
          filter: true
        },
        {
          headerName: this.$t("model.crop_field.crop_variety_id"),
          field: "crop_variety.name",
          filter: true
        },
        {
          headerName: this.$t("model.crop_field.planting_date"),
          field: "planting_date",
          filter: "agDateColumnFilter",
          cellRenderer: params => {
            return this.dateFormatter(params.value, "MMM YYYY");
          }
        },
        {
          headerName: this.$t("model.crop_field.harvest_time"),
          field: "harvest_time",
          filter: true
        },
        {
          headerName: this.$t("model.crop_field.tree_number"),
          field: "tree_number",
          filter: "agNumberColumnFilter",
          cellRenderer: params => {
            return this.numberWithPrecision(params.value);
          }
        },
        {
          headerName: this.$t("model.crop_field.crop_field_type_id"),
          field: "crop_field_type_name",
          filter: true
        },
        {
          headerName: this.$t("model.crop_field.longitude_dd"),
          field: "longitude_dd",
          filter: "agNumberColumnFilter"
        },
        {
          headerName: this.$t("model.crop_field.latitude_dd"),
          field: "latitude_dd",
          filter: "agNumberColumnFilter"
        },
        {
          headerName: this.$t("model.crop_field.longitude_dms"),
          field: "longitude_dms",
          filter: true
        },
        {
          headerName: this.$t("model.crop_field.latitude_dms"),
          field: "latitude_dms",
          filter: true
        },
        {
          headerName: this.$t("model.crop_field.zone_utm"),
          field: "zone_utm",
          filter: true
        },
        {
          headerName: this.$t("model.crop_field.longitude_utm"),
          field: "longitude_utm",
          filter: true
        },
        {
          headerName: this.$t("model.crop_field.latitude_utm"),
          field: "latitude_utm",
          filter: true
        },
        {
          headerName: this.$t("model.crop_field.altitude"),
          field: "altitude",
          filter: "agNumberColumnFilter",
          cellRenderer: params => {
            return this.numberWithPrecision(params.value);
          }
        },
        {
          headerName: this.$t("model.crop_field.former_producers"),
          autoHeight: true,
          resizable: true,
          field: "former_producers",
          cellRendererSelector: params => {
            return {
              component: FormerProducer,
              params: {
                producers: params.value
              }
            }
          }
        },
        {
          headerName: this.$t("model.crop_field.organization_crop_field.observations"),
          field: "organization_crop_field.observations",
          filter: true
        },
        {
          headerName: "",
          width: 400,
          filter: false,
          cellRendererSelector: params => {
            return {
              component: TableActions,
              params: {
                edit_label: this.$t("labels.edit_button"),
                change_producer_label: this.$t("labels.edit_producer_button"),
                editEvent: params.data.divided ? () => {} : this.goToEditCropField,
                editProducerEvent: this.goToChangeFieldOwner,
                show_destroy: true,
                destroy_label: this.$t(
                  "labels.destroy_crop_field_button_label"
                ),
                destroy_title: this.$t(
                  "labels.destroy_crop_field_button_title",
                  {
                    crop_field: params.data.name
                  }
                ),
                destroy_message: this.$t(
                  "labels.destroy_crop_field_button_message",
                  {
                    crop_field: params.data.name
                  }
                ),
                destroy_label_confirm: this.$t(
                  "labels.destroy_crop_field_button_label_confirm"
                ),
                destroyEvent: this.destroyCropField
              }
            };
          }
        }
      ]
    };
  },
  props: {
    organization_id: {},
    producer_id: {}
  },
  created() {
    this.checkIfAuthenticated().then(() =>
      this.checkIfAuthorized({
        role: this.$route.meta.role,
        permission: this.$route.meta.permission,
        entity: {
          organization_id: this.organization_id
        }
      }).then(() => {
        this.loadData();
      })
    );
  },
  computed: {
    producer_crops: function () {
      return (this.producer.producer_crops || [])
        .map(producer_crop => producer_crop.crop.name)
        .join(", ");
    }
  },
  methods: {
    loadData: async function () {
      await this.fetchAttributeProducers();
      await this.fetchProducer();
      await this.fetchProducerCropFields();
      this.load_general_data = true;
    },
    fetchProducer: async function () {
      let response = await Requester.fetchProducer(
        this.organization_id,
        this.producer_id
      );

      this.producer = response.data;
    },
    fetchAttributeProducers: async function () {
      let response = await Requester.fetchAttributeProducers(
        this.organization_id
      );

      this.attribute_producers = response.data;
    },
    fetchProducerCropFields: async function () {
      let response = await Requester.fetchProducerCropFields(
        this.organization_id,
        this.producer_id
      );

      this.crop_fields = response.data;
    },
    producerAttributeValue: function (attribute_producer_id) {
      return this.producer.producer_attributes.find(
        pa => pa.attribute_producer_id == attribute_producer_id
      );
    },
    producerAttributeValueImage: function (attribute_producer) {
      let attributeValue = this.producerAttributeValue(attribute_producer.id);
      if (attributeValue && attributeValue.attachment_path) {
        return `<img src="${baseDomain}/${attributeValue.attachment_path}" height="100" width="200" alt="${attribute_producer.label}" />`
      }

      return `<small class="text-gray-400">${attribute_producer.label} no disponible </small>`;
    },
    destroyProducer: async function () {
      this.proc_button_destroy = true;

      try {
        let response = await Requester.destroyProducer(
          this.organization_id,
          this.producer_id
        );
        this.registerSuccessNotification({
          title: response.data.success_message
        });
        this.goToListProducers();
      } catch (error) {
        if (error.response.status == 422) {
          this.loadStandardErrorMessages({
            title: error.response.data.error_title,
            messages: error.response.data.error_messages,
            fields: error.response.data.error_fields
          });
        }
      }
      this.proc_button_destroy = false;
    },
    destroyCropField: async function (crop_field) {
      crop_field.proc_button_destroy = true;

      try {
        let response = await RequesterManageCropField.destroyCropField(
          this.organization_id,
          crop_field.id
        );
        if (response.status == 200) {
          this.registerSuccessNotification({
            title: response.data.success_message
          });

          this.crop_fields.splice(this.crop_fields.indexOf(crop_field), 1);
        }
      } catch (error) {
        if (error.response.status == 422) {
          this.loadStandardErrorMessages({
            title: error.response.data.error_title,
            messages: error.response.data.error_messages,
            fields: error.response.data.error_fields
          });
        }
      }
      crop_field.proc_button_destroy = false;
    },
    goToEditProducer: function () {
      this.$router.push({
        name: "org_partners_producers_edit_producer_path",
        params: {
          organization_id: this.organization_id,
          producer_id: this.producer_id
        }
      });
    },
    goToNewCropField: function () {
      this.$router.push({
        name: "org_partners_producers_new_crop_field_path",
        params: {
          organization_id: this.organization_id,
          producer_id: this.producer_id
        }
      });
    },
    goToEditCropField: function (crop_field) {
      this.$router.push({
        name: "org_partners_producers_edit_crop_field_path",
        params: {
          organization_id: this.organization_id,
          producer_id: this.producer_id,
          crop_field_id: crop_field.id
        }
      });
    },
    goToChangeFieldOwner: function (crop_field) {
      this.$router.push({
        name: "org_partners_producers_change_field_owner_path",
        params: {
          organization_id: this.organization_id,
          producer_id: this.producer_id,
          crop_field_id: crop_field.id
        }
      });
    },
    goToListProducers: function () {
      this.$router.push({
        name: "org_partners_producers_list_producers_path",
        params: {
          organization_id: this.organization_id
        }
      });
    }
  }
};
</script>
